
import React, { useState, useEffect, useCallback } from "react";
import { Box, Container, Tabs, Tab, Paper, Typography, Skeleton, Stack, Button, Chip } from "@mui/material";

import BreadcrumbsNav from "../../components/common/BreadCrumbNav";

import ProfessionalCard from "./ProfessionalCard";
import ProfessionalFilterCard from "./ProfessionalServiceFilter";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import { Dialog, DialogTitle, DialogContent, IconButton, ToggleButtonGroup, ToggleButton, Tooltip } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { ViewModule as GridViewIcon, ViewList as ListViewIcon } from "@mui/icons-material";
import ProfessionalAbout from "./ProfessionalAbout";
import ProfessionalRating from "./ProfessionalRating";
import { motion, AnimatePresence } from "framer-motion";
import { get_proffessional_service, get_service_list as fetch_service_list } from "../../api/ControllApi";

export default function ProfessionalServiceShop() {
  const [serviceList, setServiceList] = useState([]);
  const [loading, setLoading] = useState(true);
  const { id } = useParams(); // service_id from URL
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const stateId = queryParams.get("state");
  const cityId = queryParams.get("city");
  const serviceNameParam = queryParams.get("service_name");

  const ratingVal = queryParams.get("rating");

  const [selectedService, setSelectedService] = useState(serviceNameParam || "Professional Services");

  const [filters, setFilters] = useState({
    state: stateId || "",
    city: cityId || "",
    rating: ratingVal || "",
    search: "",
  });
  const [page, setPage] = useState(1);
  const [viewType, setViewType] = useState("grid");
  const [selectedShop, setSelectedShop] = useState(null);
  const [shopDialogOpen, setShopDialogOpen] = useState(false);

  const [pagination, setPagination] = useState({
    current_page: 1,
    last_page: 1,
    total: 0
  });

  const navigate = useNavigate();

  const fetchServices = async (currentFilters = filters, currentPage = page) => {
    setLoading(true);

    try {
      const params = {
        service_id: id,
        state: currentFilters.state,
        city: currentFilters.city,
        rating: currentFilters.rating,
        name: currentFilters.search,
        page_no: currentPage,
      };

      const res = await get_proffessional_service(params);

      const rawData = res?.data;
      const list = rawData?.vendor || rawData?.data || (Array.isArray(rawData) ? rawData : []);

      setServiceList(list);
      setPagination(res?.pagination || rawData?.pagination || { current_page: 1, last_page: 1, total: 0 });

      if (list.length > 0) {
        // Find service name if available in the first vendor's service relation
        const serviceName = list[0]?.services?.service_name || "Service";
        setSelectedService(serviceName);
      } else {
        // Fallback: fetch service name from service_list if vendor list is empty
        try {
          const serviceRes = await fetch_service_list({ id: id });
          const serviceData = serviceRes?.data;
          const sName = Array.isArray(serviceData) ? serviceData[0]?.service_name : (serviceData?.service_name || "Service");
          setSelectedService(sName);
        } catch (e) {

        }
      }

    } catch (err) {

      setServiceList([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchServices(filters, page);
  }, [filters, page, id]);

  useEffect(() => {
    setPage(1);
    fetchServices(filters, 1);
  }, [id]);

  const handleFilterChange = useCallback((newFilters) => {
    setFilters(prev => ({ ...prev, ...newFilters }));
    setPage(1);
  }, []);

  function handlePageChange(newPage) {
    setPage(newPage);
    window.scrollTo({ top: 0, behavior: "smooth" });
  }

  const handleViewChange = (event, nextView) => {
    if (nextView !== null) {
      setViewType(nextView);
    }
  };

  return (
    <Container maxWidth={false} sx={{ width: { xs: '95%', md: '80%' }, mx: 'auto', py: 5 }}>
      {/* HEADER */}
      <Box
        sx={{
          mb: 3,
          mt: 3,
          display: "flex",
          flexDirection: { xs: "column", sm: "row" },
          justifyContent: "space-between",
          alignItems: { xs: "flex-start", sm: "center" },
          gap: 2,
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
          <BreadcrumbsNav />
          <Typography fontSize={{ xs: 16, sm: 18, md: 20 }}>
            Top{" "}
            <Box component="span" sx={{ color: "#95B160", fontWeight: 600 }}>
              {serviceNameParam || selectedService || "Professional Services"}
            </Box>{" "}
            Near By You
          </Typography>
        </Box>

        <ToggleButtonGroup
          value={viewType}
          exclusive
          onChange={handleViewChange}
          aria-label="view switcher"
          size="small"
          sx={{
            backgroundColor: "#fff",
            "& .Mui-selected": {
              backgroundColor: "#eaede3 !important",
              color: "#95B160 !important",
            },
          }}
        >
          <ToggleButton value="grid" aria-label="grid view">
            <Tooltip title="Grid View">
              <GridViewIcon />
            </Tooltip>
          </ToggleButton>
          <ToggleButton value="list" aria-label="list view">
            <Tooltip title="List View">
              <ListViewIcon />
            </Tooltip>
          </ToggleButton>
        </ToggleButtonGroup>
      </Box>

      {/* LAYOUT */}
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: { xs: "1fr", md: "300px 1fr" },
          gap: 6,
        }}
      >
        <Box>
          <ProfessionalFilterCard
            onFilterChange={handleFilterChange}
            selectedState={filters.state}
            selectedCity={filters.city}
            selectedRating={filters.rating}
          />
        </Box>
        <Box>
          <AnimatePresence mode="popLayout">
            <motion.div
              layout
              style={{
                display: "grid",
                gap: "24px",
                gridTemplateColumns: viewType === "list" ? "1fr" : "repeat(auto-fill, minmax(300px, 1fr))",
              }}
            >
              {loading
                ? Array.from({ length: 6 }).map((_, i) => (
                  <motion.div
                    key={`skeleton-${i}`}
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                  >
                    <Skeleton
                      variant="rectangular"
                      height={viewType === "list" ? 180 : 320}
                      sx={{ borderRadius: 3 }}
                    />
                  </motion.div>
                ))
                : serviceList.length > 0 ? serviceList.map((card) => (
                  <motion.div
                    key={card.id}
                    layout
                    initial={{ opacity: 0, scale: 0.9 }}
                    animate={{ opacity: 1, scale: 1 }}
                    exit={{ opacity: 0, scale: 0.9 }}
                    transition={{ duration: 0.3 }}
                  >
                    <ProfessionalCard
                      userid={card.user_id}
                      id={card.id}
                      name={card.name}
                      images={card.thumbnail}
                      phone={card.phone}
                      email={card.email}
                      gst={card.gst}
                      location={card.address}
                      website={card.website}
                      rating={(Number(card.reviews_avg_rating).toFixed(1) || 0)}
                      reviewCount={card.reviews_count || 0}
                      established={card?.estimate_date}
                      description={card.description}
                      whatsapp={card?.whatsapp}
                      viewType={viewType}
                      onViewShop={() => {
                        const queryParams = (serviceNameParam && id) ? `?serviceName=${encodeURIComponent(serviceNameParam)}&serviceId=${id}` : '';
                        navigate(`/professional-services/detail/${card.user_id}${queryParams}`);
                      }}
                    />
                  </motion.div>
                )) : (
                  <Box
                    sx={{
                      gridColumn: "1 / -1",
                      display: "flex",
                      flexDirection: "column",
                      justifyContent: "center",
                      alignItems: "center",
                      minHeight: "40vh",
                      textAlign: "center",
                      width: "100%",
                      py: 5,
                    }}
                  >
                    <Box
                      component="img"
                      src="/assets/images/nodatafound.png"
                      alt="No Data Found"
                      sx={{ width: { xs: 280, sm: 320, md: 350 }, mb: 2 }}
                    />
                  </Box>
                )
              }
            </motion.div>
          </AnimatePresence>
        </Box>
      </Box>

      {/* Professional Details Dialog */}
      <Dialog
        open={shopDialogOpen}
        onClose={() => setShopDialogOpen(false)}
        fullWidth
        maxWidth="md"
      >
        <DialogTitle sx={{ m: 0, p: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          {selectedShop?.name || "Professional Details"}
          <IconButton onClick={() => setShopDialogOpen(false)}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          {selectedShop && (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
              <ProfessionalAbout shopData={selectedShop} />
              <ProfessionalRating shopId={selectedShop.id} />
            </Box>
          )}
        </DialogContent>
      </Dialog>

      {/* PAGINATION */}
      <Box sx={{ display: 'flex', justifyContent: 'center', mt: 8 }}>
        {pagination.last_page > 1 && (
          <Stack direction="row" spacing={1} justifyContent="center">
            {Array.from({ length: pagination.last_page }, (_, i) => i + 1).map(
              (p) => (
                <Button
                  key={p}
                  variant={p === page ? "contained" : "outlined"}
                  onClick={() => handlePageChange(p)}
                  sx={{
                    borderRadius: "9px",
                    bgcolor: p === page ? "#95B160" : "transparent",
                    color: p === page ? "#fff" : "#95B160",
                    borderColor: "#95B160",
                    minWidth: 40,
                    height: 40,
                    "&:hover": {
                      bgcolor: p === page ? "#7a944d" : "rgba(149, 177, 96, 0.1)",
                      borderColor: "#95B160",
                    },
                  }}
                >
                  {p}
                </Button>
              )
            )}
          </Stack>
        )}
      </Box>
    </Container>
  );
}
